import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { Compass, ArrowLeft } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1502003148287-a82ef80a6abc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxhdXRoZW50aWMlMjB0cmF2ZWwlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzg2MzI1MTU0fDA&ixlib=rb-4.1.0&q=85";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/auth");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title="Reset password" description="Choose a new password for your WanderlyMe account." path="/reset-password" noindex />
    <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="travel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A3B2B]/70 to-transparent" />
        <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 text-[#FDFBF7]">
          <Compass className="w-6 h-6" />
          <span className="font-display text-2xl">WanderlyMe</span>
        </Link>
      </div>

      <div className="flex items-center justify-center p-8 grain relative">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <h1 className="font-display text-4xl tracking-tight mb-1">Set a new password</h1>
          <p className="text-muted-foreground mb-8">Choose a strong password you'll remember.</p>

          {!token ? (
            <p data-testid="reset-no-token" className="text-sm text-[#C8553D]">
              This reset link is missing its token. Please use the link from your email, or request a new one.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                required
                className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
              />
              <input
                data-testid="reset-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                required
                className="w-full px-4 py-3 rounded-xl border border-border bg-white/70 outline-none focus:border-[#1A3B2B] transition-colors"
              />
              {error && <p className="text-sm text-[#C8553D]">{error}</p>}
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading}
                className="w-full py-3 rounded-full bg-[#1A3B2B] text-[#FDFBF7] hover:bg-[#122a1f] transition-colors disabled:opacity-60"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
            </form>
          )}

          <Link
            to="/auth"
            className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-[#C8553D] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </motion.div>
      </div>
    </div>
      <Footer />
    </div>
  );
}
